import React from 'react';

import {
    approveApprovalRequest,
    createApprovalRequest,
    getApprovalInbox,
    getApprovalRequests,
    rejectApprovalRequest,
    searchAuditLog,
} from '../../../api';
import { AUDIT_DEFAULT_FILTER } from './approvalWorkflowConstants';
import { buildAuditSearchPayload } from './approvalWorkflowFormatters';

export default function useApprovalWorkflowData({ canView }) {
    const [inbox, setInbox] = React.useState([]);
    const [requests, setRequests] = React.useState([]);
    const [statusFilter, setStatusFilter] = React.useState(['PENDING']);
    const [auditEntries, setAuditEntries] = React.useState([]);
    const [auditFilter, setAuditFilter] = React.useState(AUDIT_DEFAULT_FILTER);
    const [form, setForm] = React.useState({ type: 'PAYMENT', targetId: '', comment: '' });
    const [feedback, setFeedback] = React.useState({ open: false, message: '', severity: 'success' });

    const showFeedback = React.useCallback((message, severity = 'success') => {
        setFeedback({ open: true, message, severity });
    }, []);

    const closeFeedback = React.useCallback(() => {
        setFeedback((prev) => ({ ...prev, open: false }));
    }, []);

    const loadInbox = React.useCallback(async () => {
        try {
            const res = await getApprovalInbox();
            setInbox(res.data || []);
        } catch (error) {
            showFeedback('Fehler beim Laden der Freigaben-Inbox.', 'error');
        }
    }, [showFeedback]);

    const loadRequests = React.useCallback(
        async (statuses) => {
            try {
                const res = await getApprovalRequests({ status: statuses });
                setRequests(res.data || []);
            } catch (error) {
                showFeedback('Fehler beim Laden der Freigaben.', 'error');
            }
        },
        [showFeedback]
    );

    const loadAudit = React.useCallback(
        async (filters) => {
            try {
                const payload = buildAuditSearchPayload(filters);
                const res = await searchAuditLog(payload);
                setAuditEntries(res.data || []);
            } catch (error) {
                showFeedback('Fehler beim Laden des Audit-Logs.', 'error');
            }
        },
        [showFeedback]
    );

    React.useEffect(() => {
        if (!canView) {
            return;
        }
        loadInbox();
        loadRequests(statusFilter);
        loadAudit(auditFilter);
    }, [canView, loadInbox, loadRequests, loadAudit, statusFilter, auditFilter]);

    const handleFormChange = (event) => {
        const { name, value } = event.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleCreate = async (event) => {
        event.preventDefault();
        try {
            if (!form.targetId) {
                showFeedback('Bitte geben Sie eine Referenz-ID an.', 'warning');
                return;
            }
            await createApprovalRequest({
                type: form.type,
                targetId: form.targetId,
                comment: form.comment,
            });
            showFeedback('Freigabe angelegt.');
            setForm({ type: form.type, targetId: '', comment: '' });
            await Promise.all([loadInbox(), loadRequests(statusFilter), loadAudit(auditFilter)]);
        } catch (error) {
            const message = error?.response?.data || 'Freigabe konnte nicht erstellt werden.';
            showFeedback(message, 'error');
        }
    };

    const handleDecision = async (id, decision, approve) => {
        try {
            let payload = decision;
            if (!payload || typeof payload !== 'object') {
                payload = {};
            }
            const commentPrompt = approve ? 'Kommentar zur Freigabe (optional):' : 'Grund für Ablehnung (optional):';
            const input = window.prompt(commentPrompt, payload.comment || '');
            const comment = typeof input === 'string' ? input.trim() : '';
            const requestPayload = comment ? { comment } : undefined;
            if (approve) {
                await approveApprovalRequest(id, requestPayload);
                showFeedback('Freigabe erfolgreich genehmigt.');
            } else {
                await rejectApprovalRequest(id, requestPayload);
                showFeedback('Freigabe wurde abgelehnt.', 'info');
            }
            await Promise.all([loadInbox(), loadRequests(statusFilter), loadAudit(auditFilter)]);
        } catch (error) {
            const message = error?.response?.data || 'Aktion fehlgeschlagen.';
            showFeedback(message, 'error');
        }
    };

    const handleStatusFilterChange = (event) => {
        const value = event.target.value;
        const arrayValue = Array.isArray(value) ? value : [value];
        setStatusFilter(arrayValue);
        loadRequests(arrayValue);
    };

    const handleAuditFilterChange = (event) => {
        const { name, value } = event.target;
        setAuditFilter((prev) => ({ ...prev, [name]: value }));
    };

    const refreshAudit = () => {
        loadAudit(auditFilter);
    };

    const resetAuditFilter = () => {
        setAuditFilter(AUDIT_DEFAULT_FILTER);
        loadAudit(AUDIT_DEFAULT_FILTER);
    };

    return {
        inbox,
        requests,
        statusFilter,
        auditEntries,
        auditFilter,
        form,
        feedback,
        closeFeedback,
        handleFormChange,
        handleCreate,
        handleDecision,
        handleStatusFilterChange,
        handleAuditFilterChange,
        refreshAudit,
        resetAuditFilter,
    };
}
